<template>
	<div class="registration-sub-wrapper">

		<h2 class="h3 p-t-2 m-b-xs" :class="{'m-b-2': !necessity}">In-Club</h2>
		<p v-if="necessity" class="small text-gray-5">{{ necessity }}</p>

		<!-- Course Cost Calculation -->
		<div v-if="sessionsRemaining > 0">
			<table class="table-course-cost">
				<caption class="sr-only">
					<span v-if="costAdvertised">Course Cost Calculation</span>
					<span v-else>Course Session Information</span>
				</caption>
				<tbody>
					<tr v-if="costAdvertised">
						<th scope="row" aria-label="Cost per Session">Fee/Session:</th>
						<td>${{ costAdvertised }}</td>
					</tr>
					<tr>
						<th scope="row" aria-label="Number of Sessions">No. of Sessions:</th>
						<td>{{ sessionsRemaining }}</td>
					</tr>
					<!-- TODO: Uncomment when basing totalCost on the number of participants -->
					<!-- <tr v-if="costAdvertised">
						<th scope="row" aria-label="Number of Participants">No. of Participants:</th>
						<td>{{ selectedMemberIds.length }}</td>
					</tr> -->
				</tbody>
				<tfoot v-if="totalCost > 0">
					<tr>
						<th scope="row" aria-label="Total Cost">Total Cost:</th>
						<td>${{ totalCost }}</td>
					</tr>
				</tfoot>
			</table>
		</div>

		<!-- Non-Course Cost -->
		<div v-else>
			<div v-if="costVaries">
				<p class="sidebar-cost">Price varies by package</p>
			</div>
			<div v-else-if="costAdvertised">
				<p v-if="costPrefix" class="m-b-0">{{ costPrefix }}</p>
				<p class="sidebar-cost"><span class="sidebar-cost-unit">$</span><span class="sidebar-cost-value">{{ costAdvertised }}</span><span v-if="costSuffix"> / {{ costSuffix }}</span></p>
			</div>
		</div>

		<!-- Spots Available / Waitlist -->
		<span v-if="registerCta && !registerCta.isExternal">
			<p v-if="remainingSpots === 0" class="text-danger small m-b-sm"><span class="ico-alert-sm"></span>This class is full</p>
			<p v-else><strong>{{ remainingSpots }}</strong> spot<span v-if="remainingSpots !== 1">s</span> available</p>

			<p v-if="remainingSpots === 0 && waitlistFull" class="text-danger small"><span class="ico-alert-sm"></span>The waitlist is full</p>

			<p v-if="remainingSpots === 0 && !hasWaitlist" class="small"><span class="ico-close-sm"></span>No waitlist available</p>

			<p v-if="remainingSpots === 0 && hasWaitlist && !waitlistHasCapacity"><strong>{{ totalWaitlisted }}</strong> people on the waitlist</p>
			<p v-else-if="remainingSpots === 0 && hasWaitlist && !waitlistFull"><strong>{{ waitlistCapacity - totalWaitlisted }} of {{ waitlistCapacity }}</strong> waitlist spots available</p>
		</span>

		<!-- Already Registered -->
		<div v-for="registeredMember in registeredMembers" :key="registeredMember.id" class="m-b-2">
			<div class="m-b-sm">
				<div v-if="registeredMember.spot"><b>{{ registeredMember.name }}</b> reserved spot {{ registeredMember.spot }}</div>
				<div v-else-if="registeredMember.spotWaitlist"><b>{{ registeredMember.name }}</b> reserved waitlist spot {{ registeredMember.spotWaitlist }}</div>
				<div v-else><b>{{ registeredMember.name }}</b> reserved</div>
			</div>

			<!-- Edit and Cancel -->
			<div class="btn-wrap" v-if="(registeredMember.editCtas[0] && registeredMember.editCtas[0].method === 'online') || (registeredMember.cancelCtas[0] && registeredMember.cancelCtas[0].method === 'online')">
				<button v-if="registeredMember.editCtas[0] && registeredMember.editCtas[0].method === 'online'" class="btn btn-primary btn-sm btn-block-xs" @click="handleCtaClicked(registeredMember.editCtas[0])">{{ buildCtaButtonText(registeredMember.editCtas[0]) }}</button>
				<button v-if="registeredMember.cancelCtas[0] && registeredMember.cancelCtas[0].method === 'online'" class="btn btn-primary btn-sm btn-block-xs" @click="handleCtaClicked(registeredMember.cancelCtas[0])" data-testid="cancelBtn">{{ buildCtaButtonText(registeredMember.cancelCtas[0]) }}</button>
			</div>
			<div v-if="registeredMember.cancelCtas[0] && registeredMember.cancelCtas[0].method === 'phone'" v-html="buildPhoneCta(registeredMember.cancelCtas[0])"></div>
		</div>

		<!-- Unregistered (Eligible and Ineligible) -->
		<div class="c-inputs-stacked m-b-1" v-if="(registerCta && !registerCta.isExternal) && unregisteredMembers.length > 0">
			<fieldset class="form-group m-b-0">
				<legend class="h6 m-b-sm">Select <span v-if="remainingSpots === 0 && hasWaitlist">Waitlist</span> Participants</legend>
				<label class="c-input c-checkbox" v-for="unregisteredMember in unregisteredMembers" :key="unregisteredMember.id" data-testid="participantLabel">
					<input type="checkbox" data-testid="participantCheckBox" v-model="selectedMemberIds" :value="unregisteredMember.id" :disabled="!unregisteredMember.isEligible">
					<span class="c-indicator"></span>
					{{ unregisteredMember.name }}
					<span v-if="!unregisteredMember.isEligible" data-toggletip data-char="i" data-position="bottom" data-btn-label="Reason for ineligibility">{{ unregisteredMember.reason }}</span>
				</label>
			</fieldset>
		</div>

		<p v-if="unregisteredMembers.length === 1 && !unregisteredMembers[0].isEligible">{{ unregisteredMembers[0].reason }}</p>

		<InlineAlert
			v-if="validationShow"
			:errors="validationResults"
			:types="validationTypes.participants"/>

		<!-- Reserve / Add to Waitlist / Log In to Reserve -->
		<p class="btn-wrap m-b-2" v-if="showRegisterCta && registerCta && registerCta.method === 'online'">
			<button class="btn btn-primary btn-sm btn-block-xs" @click="handleCtaClicked(registerCta)" data-testid="reserveButton">{{ buildCtaButtonText(registerCta) }}<span v-if="selectedMemberIds.length > 1"> for {{ selectedMemberIds.length }}</span></button>
		</p>
		<p v-if="showRegisterCta && registerCta && registerCta.method === 'online' && registerCta.isExternal">Reservations are managed through an external website.</p>
		<p v-if="showRegisterCta && registerCta && registerCta.method === 'phone'" v-html="buildPhoneCta(registerCta)"></p>

		<!-- Loading Spinner -->
		<div role="alert" aria-live="assertive">
			<transition name="fade" appear>
				<div class="spinner-overlay spinner-overlay-section" data-testid="sectionSpinner" v-show="isWaitingRegistration">
					<p class="sr-only">Registration information is loading…</p>
					<LoadingSpinner
						:config="{scale: 0.4}"
					/>
				</div>
			</transition>
		</div>
	</div>
</template>

<script>
import Vue, { defineComponent } from 'vue';
import { validationMixin } from '../mixins/validation.mixin';
import _ from 'lodash';
import { RuleNames } from '@/services/rules.service.config';
import { NotificationService } from '@/services/notification.service';
import { FrameworkService } from '@/services/framework.service';
import InlineAlert from '@/components/schreg-common/InlineAlert.vue';
import LoadingSpinner from '@/components/schreg-common/LoadingSpinner.vue';

export default defineComponent({
	name: 'RegistrationSub',
	components: {
		InlineAlert,
		LoadingSpinner
	},
	mixins: [
		validationMixin
	],
	props: {
		sessionsRemaining: {
			default: 0,
			type: Number
		},
		costPrefix: {
			default: '',
			type: String
		},
		costAdvertised: {
			default: '',
			type: String
		},
		costSuffix: {
			default: '',
			type: String
		},
		costVaries: {
			default: false,
			type: Boolean
		},
		userAuthenticated: {
			default: false,
			type: Boolean
		},
		regData: {
			default: null,
			type: Object
		},
		isWaitingRegistration: {
			default: true,
			type: Boolean
		}
	},
	data() {
		return {
			isRegistrable: false,
			remainingSpots: 0,
			totalWaitlisted: 0,
			waitlistCapacity: null,
			hasWaitlist: false,
			waitlistHasCapacity: false,
			waitlistFull: false,
			necessity: '',
			registerCta: null,
			registeredMembers: [],
			unregisteredMembers: [],
			selectedMemberIds: [],
			showRegisterCta: false,
			clubPhone: '',
			validationTypes: {
				participants: [RuleNames.participantSelection, RuleNames.adultMinor]
			},
			validationResults: []
		};
	},
	computed: {
		validationShow: function() {
			return this.validationResults.length > 0;
		},
		totalCost: function() {
			if (!this.costAdvertised) return 0;
			// return this.sessionsRemaining * parseFloat(this.costAdvertised) * this.selectedMemberIds.length;
			return this.sessionsRemaining * parseFloat(this.costAdvertised); // TODO: Switch back to the above (multiplying by the number of participants) when we show the number of participants
		}
	},
	watch: {
		regData: function(regData) {
			if (!regData) return;
			this.isRegistrable = this.regData.hasRegistration;
			if (!this.isRegistrable) return;

			if (this.regData.notifications.length) {
				NotificationService.info(_.map(this.regData.notifications, n => n.message).join(' ')).trigger();
			}

			this.buildRules({ validation: { rules: this.regData.rules } });

			this.necessity = this.regData.necessity;
			this.remainingSpots = this.regData.remainingSpots;
			this.totalWaitlisted = this.regData.totalWaitlisted;
			this.waitlistCapacity = this.regData.waitlistCapacity;
			this.hasWaitlist = this.regData.hasWaitlist;
			this.waitlistHasCapacity = this.waitlistCapacity !== undefined;
			this.waitlistFull = this.hasWaitlist && this.waitlistHasCapacity && this.waitlistCapacity === this.totalWaitlisted;
			this.registeredMembers = this.regData.registeredMembers;
			this.unregisteredMembers = this.regData.unregisteredMembers;
			this.clubPhone = this.regData.clubPhone;

			// Process Register CTA to replace with login URL as necessary (cancel URL gets added after adding member IDs)
			this.registerCta = this.regData.registerCta || null;

			if (this.registerCta) {
				if (!this.userAuthenticated && !this.registerCta.isExternal) {
					this.registerCta.url = FrameworkService.globalUrl('loginResource');
				}
			}

			for (let registeredMember of this.registeredMembers) {
				// Process Edit CTAs to replace with login URL or add cancel URL as necessary
				if (registeredMember.editCtas.length > 0) {
					if (!this.userAuthenticated) {
						registeredMember.editCtas[0].url = FrameworkService.globalUrl('loginResource');
					} else if (!registeredMember.editCtas[0].isExternal && registeredMember.editCtas[0].url) {
						registeredMember.editCtas[0].url = FrameworkService.addCancelUrl(registeredMember.editCtas[0].url);
					}
				}

				// Process Cancel CTAs to add login URL as necessary
				if (registeredMember.cancelCtas.length > 0) {
					if (!this.userAuthenticated) {
						registeredMember.cancelCtas[0].url = FrameworkService.globalUrl('loginResource');
					}
				}
			}

			const eligibleMembers = _.filter(this.unregisteredMembers, 'isEligible');

			// If the logged-in member is eligible, select them by default (and show the CTAs)
			const loggedInMemberId =  FrameworkService.data('profile.memberId');
			if (_.find(eligibleMembers, ['id', loggedInMemberId]) && !_.includes(this.selectedMemberIds, loggedInMemberId)) {
				this.selectedMemberIds.push(loggedInMemberId);
				this.showRegisterCta = true;
			}

			// If there's an eligible member besides the logged-in member, show CTAs
			const eligibleOtherMembers = _.reject(eligibleMembers, ['id', loggedInMemberId]);
			if (eligibleOtherMembers.length > 0) {
				this.showRegisterCta = true;
			}

			// If the user is unauthenticated and there is a register CTA, show CTAs (for "Log In to Reserve")
			if (!this.userAuthenticated && this.registerCta) {
				this.showRegisterCta = true;
			}

			// If registration is external, always show CTAs
			if (this.registerCta.isExternal) {
				this.showRegisterCta = true;
			}

			this.$nextTick(() => {
				$('[data-toggletip]').myltToggletip(); // Force toggletips to render
			});
		}
	},
	methods: {
		handleCtaClicked(cta) {
			this.validationResults.length = 0;
			if (cta.type === 'register' && this.userAuthenticated && !cta.isExternal) {

				// Run validation rules
				if (!this.validateComplete()) {
					return;
				}

				if (this.selectedMemberIds.length > this.remainingSpots && (this.remainingSpots > 0 || !this.hasWaitlist)) {
					var multipleAvailable = this.remainingSpots > 1;
					this.validationResults.push({
						from: 'client',
						name: RuleNames.participantSelection,
						message: 'There ' + (multipleAvailable ? 'are' : 'is') + ' only ' + this.remainingSpots + ' spot' + (multipleAvailable ? 's' : '') + ' available. Please select only ' + this.remainingSpots + ' member' + (multipleAvailable ? 's.' : '.') + (this.hasWaitlist ? ' You may add members to the waitlist after reservations have been made.' : '')
					});
					return;
				} else if (this.selectedMemberIds.length > 0) {
					this.$emit('reserveCtaClicked');
					for (let selectedMemberId of this.selectedMemberIds) {
						cta.url = cta.url + '&memberId=' + encodeURIComponent(selectedMemberId);
					}
					cta.url = FrameworkService.addCancelUrl(cta.url);
				} else {
					this.validationResults.push({
						from: 'client',
						name: RuleNames.participantSelection,
						message: 'You must select at least one participant'
					});
					return;
				}
			}
			this.$emit('ctaClicked', cta);
		},

		buildCtaButtonText(cta) {
			return cta.text;
		},

		buildPhoneCta(cta) {
			if (cta.text.indexOf('{0}') > -1 && this.clubPhone) {
				const phoneDisplay = Vue.filter('phoneFilter')(this.clubPhone, 'display');
				const phoneUrl = Vue.filter('phoneFilter')(this.clubPhone, 'telUrl');
				const phoneAria = Vue.filter('phoneFilter')(this.clubPhone, 'ariaLabel');
				const txt = `<a href="${phoneUrl}" :aria-label="${phoneAria}">${phoneDisplay}</a>`;
				return cta.text.replace('{0}', txt);
			}
			return cta.text;
		}
	}
});
</script>

<style scoped lang="scss">
@import "@/common/styles/loading.scss";
@import "@/common/styles/btn-block-xs.scss";

// CTA Sidebar Loading Spinners
.registration-sub-wrapper {
	position: relative;
	min-height: 13rem;
}
.spinner-overlay-section {
	position: absolute;
	height: 100%;
	backdrop-filter: blur(4px);
	// Extend overlay to the edges so blur isn't cut off
	width: auto;
	top: 1.5rem;
	left: -1rem;
	right: -1rem;
}
.spinner-overlay {
	z-index: 1040;
}

.table-course-cost {
	line-height: 1.75;
	margin-bottom: 1.5rem;

	tbody tr:last-child th,
	tbody tr:last-child td {
		padding-bottom: .25rem;
	}


	th {
		font-weight: normal;
	}
	td {
		padding-left: 0.5rem;
		text-align: right;
	}
	tfoot th,
	tfoot td {
		border-top: 1px solid $gray-11;
		font-weight: bold;
		line-height: 2;
	}
}

.sidebar-cost {
	font-weight: 600;
	line-height: 1;
	font-size: 0.75rem;
}
.sidebar-cost-unit {
	position: relative;
	vertical-align: top;
	top: 0.125rem;
}
.sidebar-cost-value {
	font-weight: normal;
	font-size: 1.25rem;
}

@include media-breakpoint-up(md) {
	.sidebar-cost {
		font-size: 0.875rem;
	}
	.sidebar-cost-unit {
		top: 0.125rem;
	}
	.sidebar-cost-value {
		font-size: 1.5rem;
	}
}

@include media-breakpoint-up(lg) {
	.sidebar-cost {
		font-size: 1rem;
	}
	.sidebar-cost-unit {
		top: 0.175rem;
	}
	.sidebar-cost-value {
		font-size: 2rem;
	}
}
</style>
